// Piezas compartidas de la plataforma interna.
import { Copy, FileText, MessageCircle, Phone } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ETIQUETA_VEREDICTO,
  linkLlamar,
  linkWhatsApp,
  telefonoLegible,
} from "@/reclutamiento/logica";
import { resolverCv } from "@/reclutamiento/cv";
import { mensajeDeError } from "./errores";
import type { EstadoCriterio, Mensaje, Veredicto } from "@/reclutamiento/tipos";

/** Marca del semáforo: círculo ✓ (cumple), triángulo ! (revisar), octágono ✗ (descartado). No depende solo del color. */
export const MarcaSemaforo = ({
  tipo,
  size = 18,
}: {
  tipo: "ok" | "revisar" | "bad" | Veredicto | EstadoCriterio;
  size?: number;
}) => {
  const k =
    tipo === "ok" || tipo === "cumple"
      ? "ok"
      : tipo === "revisar" || tipo === "falta"
        ? "yel"
        : "red";
  if (k === "ok")
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="shrink-0">
        <circle cx="10" cy="10" r="9.2" fill="var(--ok)" />
        <path d="M5.8 10.4l2.8 2.8 5.6-6" fill="none" stroke="var(--glyph)" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  if (k === "yel")
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="shrink-0">
        <path d="M10 1.4 19.2 18H.8z" fill="var(--warn)" stroke="var(--warn)" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M10 7.2v4.6" stroke="var(--warn-glyph)" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="10" cy="14.7" r="1.25" fill="var(--warn-glyph)" />
      </svg>
    );
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="shrink-0">
      <path d="M6.2 1h7.6L19 6.2v7.6L13.8 19H6.2L1 13.8V6.2z" fill="var(--bad)" />
      <path d="M6.8 6.8l6.4 6.4M13.2 6.8l-6.4 6.4" stroke="var(--glyph)" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
};

export const PildoraVeredicto = ({
  veredicto,
  texto,
  className,
}: {
  veredicto: Veredicto;
  texto?: string;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
      veredicto === "cumple" ? "pill-ok" : veredicto === "revisar" ? "pill-revisar" : "pill-bad",
      className,
    )}
  >
    <MarcaSemaforo tipo={veredicto} size={13} />
    {texto ?? ETIQUETA_VEREDICTO[veredicto]}
  </span>
);

export const Pildora = ({
  tono,
  children,
}: {
  tono: "ok" | "revisar" | "bad" | "neutro" | "acento";
  children: ReactNode;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
      tono === "ok" && "pill-ok",
      tono === "revisar" && "pill-revisar",
      tono === "bad" && "pill-bad",
      tono === "neutro" && "bg-muted text-muted-foreground",
      tono === "acento" && "bg-[var(--acento-soft)] text-[var(--acento)]",
    )}
  >
    {children}
  </span>
);

export const IconosCriterios = ({
  criterios,
}: {
  criterios: { estado: EstadoCriterio }[];
}) => (
  <span className="inline-flex items-center gap-0.5" aria-label={criterios.map((c) => (c.estado === "ok" ? "cumple" : c.estado === "falta" ? "por confirmar" : "no cumple")).join(", ")}>
    {criterios.map((c, i) => (
      <MarcaSemaforo key={i} tipo={c.estado} size={13} />
    ))}
  </span>
);

export const Kpi = ({
  titulo,
  valor,
  detalle,
  tono,
}: {
  titulo: string;
  valor: ReactNode;
  detalle?: ReactNode;
  tono?: "bad" | "ok";
}) => (
  <div className="rounded-xl border bg-card p-4 shadow-xs">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</p>
    <p
      className={cn(
        "num mt-1 text-2xl font-semibold",
        tono === "bad" && "text-[var(--bad-ink)]",
        tono === "ok" && "text-[var(--ok-ink)]",
      )}
    >
      {valor}
    </p>
    {detalle ? <p className="mt-1 text-xs text-muted-foreground">{detalle}</p> : null}
  </div>
);

export const Seccion = ({
  titulo,
  children,
  acciones,
  className,
}: {
  titulo: ReactNode;
  children: ReactNode;
  acciones?: ReactNode;
  className?: string;
}) => (
  <section className={cn("rounded-xl border bg-card p-4", className)}>
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-base font-semibold">{titulo}</h2>
      {acciones}
    </div>
    {children}
  </section>
);

export const Encabezado = ({
  ceja,
  titulo,
  sub,
  acciones,
}: {
  ceja?: string;
  titulo: ReactNode;
  sub?: ReactNode;
  acciones?: ReactNode;
}) => (
  <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
    <div className="min-w-0">
      {ceja ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{ceja}</p>
      ) : null}
      <h1 className="text-2xl font-semibold tracking-tight">{titulo}</h1>
      {sub ? <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{sub}</p> : null}
    </div>
    {acciones ? <div className="flex flex-wrap gap-2">{acciones}</div> : null}
  </div>
);

export const BotonesContacto = ({
  telefono,
  nombre,
  puesto,
  empresa,
  grande,
}: {
  telefono: string | null;
  nombre: string | null;
  puesto?: string | null;
  empresa?: string | null;
  grande?: boolean;
}) => {
  const wa = linkWhatsApp(telefono, nombre, puesto, empresa);
  const tel = linkLlamar(telefono);
  if (!wa || !tel) return <span className="text-xs text-muted-foreground">Sin teléfono</span>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button asChild size={grande ? "lg" : "sm"} className="bg-[var(--ok)] text-white hover:opacity-90">
        <a href={wa} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          <MessageCircle className="size-4" /> WhatsApp
        </a>
      </Button>
      <Button asChild size={grande ? "lg" : "sm"} variant="outline">
        <a href={tel} onClick={(e) => e.stopPropagation()}>
          <Phone className="size-4" /> Llamar
        </a>
      </Button>
      {grande ? (
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="num">{telefonoLegible(telefono)}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigator.clipboard?.writeText(telefono ?? "")}
            aria-label="Copiar teléfono"
          >
            <Copy className="size-3.5" /> Copiar
          </Button>
        </span>
      ) : null}
    </div>
  );
};

/** CV dentro de la página (PDF). En demo se genera; en Supabase es liga firmada del bucket privado. */
export const VisorCv = ({ cvUrl, alto = 520 }: { cvUrl: string | null; alto?: number }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    let creada: string | null = null;
    setUrl(null);
    setError(null);
    resolverCv(cvUrl)
      .then((u) => {
        if (!vivo) return;
        if (u?.startsWith("blob:")) creada = u;
        setUrl(u);
      })
      .catch((e) => vivo && setError(String(e?.message ?? e)));
    return () => {
      vivo = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [cvUrl]);
  if (!cvUrl) return <p className="text-sm text-muted-foreground">Sin CV todavía.</p>;
  if (error) return <p className="text-sm text-[var(--bad-ink)]">{error}</p>;
  if (!url) return <p className="text-sm text-muted-foreground">Cargando CV…</p>;
  return (
    <div className="space-y-2">
      <iframe title="CV" src={url} className="w-full rounded-lg border bg-white" style={{ height: alto }} />
      <div className="flex gap-2">
        <Button asChild variant="outline" size="sm">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <FileText className="size-4" /> Ver en grande
          </a>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <a href={url} download="cv.pdf">
            Descargar
          </a>
        </Button>
      </div>
    </div>
  );
};

export const Conversacion = ({
  mensajes,
  nombre,
}: {
  mensajes: Mensaje[] | null | undefined;
  nombre?: string | null;
}) => {
  if (!mensajes?.length) return <p className="text-sm text-muted-foreground">Sin conversación registrada.</p>;
  return (
    <div className="flex max-h-96 flex-col gap-2 overflow-y-auto rounded-lg border bg-muted/30 p-3">
      {mensajes.map((m, i) => (
        <div
          key={i}
          className={cn(
            "max-w-[85%] rounded-xl px-3 py-2 text-sm",
            m.de === "bot" ? "conv-bot self-start" : "self-end bg-card border",
          )}
        >
          <small className="block text-[11px] font-semibold text-muted-foreground">
            {m.de === "bot" ? "Bot" : (nombre ?? "Persona")}
          </small>
          {m.texto}
        </div>
      ))}
    </div>
  );
};

/** Una consulta falló: se dice en la pantalla (nunca una lista vacía en silencio). */
export const ErrorDeCarga = ({
  error,
  que = "los datos",
  onReintentar,
}: {
  error: unknown;
  que?: string;
  onReintentar?: () => void;
}) => (
  <div role="alert" className="mb-3 rounded-lg border border-[var(--bad)] bg-card p-3 text-sm">
    <p className="font-semibold">No se pudieron cargar {que}.</p>
    <p className="mt-1 text-muted-foreground">{mensajeDeError(error)}</p>
    {onReintentar ? (
      <Button size="sm" variant="outline" className="mt-2" onClick={() => onReintentar()}>
        Reintentar
      </Button>
    ) : null}
  </div>
);
